import React, { useState } from 'react';
import classNames from 'classnames';
import lodashCompact from 'lodash/fp/compact';

import {
  RENTAL_APPLICATION_PAYSTUB_FILES_COUNT,
  RENTAL_APPLICATION_BANK_STATEMENT_FILES_COUNT,
  PROOF_OF_INCOME_PAYTSTUBS,
  PROOF_OF_INCOME_OFFER_LETTER,
  PROOF_OF_INCOME_BANK_STATEMENTS,
  PROOF_OF_INCOME_ADDITIONAL_DOCUMENTS,
} from 'common/householdApplication/incomeHelpers';
import { INCOME_VERIFICATION_STATUS_FAILED } from 'common/incomeVerificationConstants';

import SrBox from 'client/components/SrBox';
import SrText from 'client/components/SrText';
import SrFlex from 'client/components/SrFlex';
import SrCheckbox from 'client/components/SrCheckbox';
import SrSeparator from 'client/components/SrSeparator';
import SrErrorMessage from 'client/components/SrErrorMessage/SrErrorMessage';
import SrCollapsibleOption from 'client/components/SrCollapsibleOption/SrCollapsibleOption';
import SrRentalApplicationVerifyIncome from 'client/components/SrRentalApplicationVerifyIncome';
import SrButton, {
  SR_BUTTON_HEIGHT_MEDIUM,
  SR_BUTTON_THEME_ADOBE_BLUE,
  SR_BUTTON_THEME_ADOBE_BLUE_BORDER,
} from 'client/components/SrButton/SrButton';
import SrUploadDocumentsApplicationFlow, {
  getIncomeSectionDocumentsCountByType,
} from 'client/components/SrUploadDocumentsApplicationFlow/SrUploadDocumentsApplicationFlow';

import './SrVerifyIncomeOptions.less';

const SrVerifyIncomeOptions = ({
  dispatch,
  currentUser,
  householdApplicationId,
  listing,
  incomeVerification,
  retryIncomeVerificationFailed,
  retryIncomeVerification,
  onIncomeVerificationIframeClose,
  reloadIncomeVerificationData,
  formFieldErrors,
  formikBag,
  formFields,
  incomeVerificationEnabled,
}) => {

  const [isActive, setIsActive] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [isOpenUploadDocsModal, setIsOpenUploadDocsModal] = useState(false);

  const toggleActive = () => {
    if (isActive && hasError) {
      setHasError(false);
    }
    setIsActive(false);
  };

  const validateAcknowledge = () => {
    if (isActive) {
      setHasError(true);
      const element = global.document.getElementById('error-ref');
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }

      return false;
    }

    return true;
  };

  const onOpenManualDocs = () => {
    if (!validateAcknowledge()) {
      return;
    }

    setIsOpenUploadDocsModal(true);
  };

  const paystubs = lodashCompact(formikBag.values.paystubPhotos).length > 0
    ? formikBag.values.paystubPhotos
    : incomeVerification?.paystubs;
  const documentsHaveProgress = lodashCompact([
    ...(paystubs || []),
    ...(formikBag.values.bankStatementFileUrls || []),
    ...(formikBag.values.offerLetterFileUrl ? [formikBag.values.offerLetterFileUrl] : []),
    ...(formikBag.values.additionalProofOfIncomePhotos || []),
  ]).length > 0;
  const uploadDocsButtonTheme = documentsHaveProgress
    ? SR_BUTTON_THEME_ADOBE_BLUE_BORDER
    : SR_BUTTON_THEME_ADOBE_BLUE;

  return (
    <>
      <div
        styleName={classNames('container', { isActive, hasError })}
        id='error-ref'
      >
        <SrBox
          padding='16px'
        >
          <SrText
            color={isActive ? 'blue300' : '#132630'}
            size='16'
            weight='800'
          >
            Your verification options
          </SrText>
          <SrText
            paddingTop='10px'
            weight='800'
            size='16'
            lineHeight='30px'
            display='block'
          >
            <SrText
              paddingRight='9px'
            >
              💪
            </SrText>
            Improve your odds
            <SrText
              weight='500'
            >
              {' '}of getting accepted.
            </SrText>
            <SrBox paddingTop='11px' />
            <SrText
              paddingRight='9px'
            >
              🤑
            </SrText>
            Prove
            <SrText
              weight='500'
            >
              {' '}your income below.
            </SrText>
            <SrBox paddingTop='11px' />
            <SrText
              paddingRight='9px'
            >
              🍀
            </SrText>
            Get approved
            <SrText
              weight='500'
            >
              {' '}by using multiple options!
            </SrText>
          </SrText>
          <SrBox paddingTop='5px' />
          <SrCheckbox
            onClick={toggleActive}
            checked={!isActive}
          >
            <SrText
              color={isActive ? '#132630' : 'green'}
              weight={isActive ? '500' : '800'}
              lineHeight='10px'
            >
              I Understand
            </SrText>
          </SrCheckbox>
        </SrBox>
        {hasError && (
          <div
            styleName={classNames('error')}
          >
            <SrText
              color='white'
              weight='bold'
              fontSize='16px'
            >
              Press “I Understand” to continue with
              your verification
            </SrText>
          </div>
        )}
      </div>
      <SrBox marginTop='16px' />
      {
        incomeVerificationEnabled && (
          <>
            <SrRentalApplicationVerifyIncome
              dispatch={dispatch}
              currentUser={currentUser}
              householdApplicationId={householdApplicationId}
              listing={listing}
              incomeVerification={incomeVerification}
              retryIncomeVerificationFailed={retryIncomeVerificationFailed}
              onRetryIncomeVerification={retryIncomeVerification}
              onIframeClose={onIncomeVerificationIframeClose}
              reloadIncomeVerificationData={reloadIncomeVerificationData}
              isVerificationOptionsUnderstood={isActive}
              validateAcknowledge={validateAcknowledge}
            />
            <SrBox marginTop='16px' />
          </>
        )
      }
      <SrCollapsibleOption
        title='Upload verifying documents'
        buttonText='Upload Documents'
        isActive={!isActive}
        icon='docs-icon'
        onClick={validateAcknowledge}
        changeBackground={false}
        initialCollapsedState={false}
      >
        <>
          {
            documentsHaveProgress
              ? (
                <SrUploadDocumentsProgress
                  formikBag={formikBag}
                />
              )
              : (
                <>
                  <SrText
                    weight='500'
                    size='16'
                    lineHeight='24px'
                    color={!isActive ? null : 'greyMonochrome'}
                  >
                    Use this option:
                    <br />
                    <SrBox padding='20px 20px 0 20px'>
                      <SrText
                        weight='500'
                        size='16'
                        lineHeight='24px'
                        color={!isActive ? null : 'greyMonochrome'}
                      >
                        <ul>
                          <li>
                            To verify other proof of income.
                          </li>
                          <li>
                            If you linked with options above, you can still provide more documents below.
                          </li>
                        </ul>
                      </SrText>
                    </SrBox>
                    <SrBox paddingTop='20px' />
                    <SrText
                      weight='500'
                      size='16'
                      lineHeight='24px'
                      color={!isActive ? null : 'greyMonochrome'}
                    >
                      <span styleName='textBold'>
                        Documents we ask for:
                      </span>
                      <SrBox padding='10px 0 0 20px'>
                        <ul>
                          <li>
                            3 most recent
                            <span styleName='textBold'>
                              {' '}paystubs
                            </span>
                          </li>
                          <li>
                            6 most recent
                            <span styleName='textBold'>
                              {' '}bank account statements
                            </span>
                          </li>
                          <li>
                            Company
                            <span styleName='textBold'>
                              {' '}offer letter
                            </span>
                          </li>
                          <li>
                            <span styleName='textBold'>
                              Verifying documents
                            </span>
                            {' '}for child support, government benefits, business ownership, etc.
                          </li>
                        </ul>
                      </SrBox>
                    </SrText>
                  </SrText>
                </>
              )
          }
          <SrBox margin='32px 0 16px'>
            <SrButton
              theme={uploadDocsButtonTheme}
              hasBoxShadow
              minWidth='100%'
              heightType={SR_BUTTON_HEIGHT_MEDIUM}
              onClick={onOpenManualDocs}
            >
              Upload Documents
            </SrButton>
          </SrBox>
          <SrText
            weight='500'
            color={isActive ? null : 'greyMonochrome'}
            size='16'
            lineHeight='18.45px'
            width='100%'
            display='block'
            align='center'
          >
            🔒 Private, secure, and encrypted
          </SrText>
        </>
      </SrCollapsibleOption>
      {formFieldErrors.incomeVerificationVOIE
        && formikBag.touched.incomeVerificationVOIE && (
        <SrErrorMessage>
          {formFieldErrors.incomeVerificationVOIE}
        </SrErrorMessage>
      )}
      {isOpenUploadDocsModal && (
        <SrUploadDocumentsApplicationFlow
          onClose={() => setIsOpenUploadDocsModal(false)}
          formikBag={formikBag}
          formFields={formFields}
          incomeVerificationEnabled={incomeVerificationEnabled}
        />
      )}
    </>
  );
};

const SrUploadDocumentsProgress = ({ formikBag }) => {
  const payrollLinked = (
    formikBag.values.incomeVerificationVOIE
    && formikBag.values.incomeVerificationVOIE.status !== INCOME_VERIFICATION_STATUS_FAILED
  );
  const reportData = [
    {
      label: 'Paystubs',
      value: payrollLinked
        ? 'Payroll Linked'
        : (
          getIncomeSectionDocumentsCountByType(PROOF_OF_INCOME_PAYTSTUBS, formikBag)
          + ' of '
          + RENTAL_APPLICATION_PAYSTUB_FILES_COUNT
        ),
    },
    {
      label: 'Bank Statement',
      value: (
        getIncomeSectionDocumentsCountByType(PROOF_OF_INCOME_BANK_STATEMENTS, formikBag)
        + ' of '
        + RENTAL_APPLICATION_BANK_STATEMENT_FILES_COUNT
      ),
    },
    {
      label: 'Offer letter',
      value: (
        getIncomeSectionDocumentsCountByType(PROOF_OF_INCOME_OFFER_LETTER, formikBag)
      ),
    },
    {
      label: 'Other',
      value: (
        getIncomeSectionDocumentsCountByType(PROOF_OF_INCOME_ADDITIONAL_DOCUMENTS, formikBag)
      ),
    },
  ];
  return (
    <SrBox
      margin='32px 0 2px 0'
    >
      <SrBox marginBottom='8px'>
        <SrText
          weight='500'
          fontSize='16px'
          lineHeight='18px'
        >
          Uploaded documents
        </SrText>
      </SrBox>
      <SrSeparator backgroundColor='#D9D9D9' />
      <SrFlex width='100%'>
        <SrFlex
          width='100%'
          justify='between'
          direction='col'
        >
          {
            reportData.map((item) =>
              (
                <SrFlex
                  width='100%'
                  justify='between'
                  marginBottom='16px'
                >
                  <SrText
                    weight='500'
                    fontSize='16px'
                    lineHeight='18px'
                    color='grey'
                  >
                    {item.label}
                  </SrText>
                  <SrText
                    weight='500'
                    fontSize='16px'
                    lineHeight='18px'
                  >
                    {item.value}
                  </SrText>
                </SrFlex>
              )
            )
          }
        </SrFlex>
        <SrBox />
      </SrFlex>
      <SrText
        weight='500'
        fontSize='14px'
        lineHeight='20px'
        width='100%'
        display='block'
      >
        You can still use the other options to provide more verification if applicable. Other options above
      </SrText>
      <SrBox marginTop='28px' />
    </SrBox>
  );
};

SrVerifyIncomeOptions.displayName = 'SrVerifyIncomeOptions';

export default SrVerifyIncomeOptions;
